import React from 'react';
import { Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { InlineLoadingState, LoadingSpinner } from '../../../components/LoadingSpinner';
import { formatDateTimeDDMMYYYY } from '../../../utils/date';
import { DkvConnectModal, type DkvEditSettings } from '../components/DkvConnectModal';
import { SettingsCard } from '../components/SettingsCard';
import { SettingsCardSavedStatus } from '../components/SettingsCardSavedStatus';
import { SettingsStatusChip } from '../components/SettingsStatusChip';
import {
  SETTINGS_BTN_DANGER_OUTLINE,
  SETTINGS_BTN_OUTLINE,
  SETTINGS_BTN_PRIMARY,
  SETTINGS_FIELD_LABEL,
} from '../utils/settingsSectionStyles';

type DkvSyncStats = {
  rangeStart: string;
  rangeEnd: string;
  fetched: number;
  staged: number;
  matched: number;
  fuelImported: number;
  cardsSeen: number;
  cardsLinked: number;
  unmatchedPlates: string[];
  unmatchedCards: string[];
};

type DkvSettings = {
  clientId: string;
  clientIdMasked: string;
  hasClientSecret: boolean;
  hasSubscriptionKey: boolean;
  customerIds: string[];
  syncEnabled: boolean;
  complete: boolean;
  lastStatusCode: number | null;
  lastStatusMessage: string | null;
  lastTestedAt: string | null;
  lastSyncAt: string | null;
  lastSyncStatus: 'ok' | 'error' | null;
  lastSyncError: string | null;
  lastSyncStats: DkvSyncStats | null;
};

type DkvCounts = { staged: number; unmatched: number; fuel: number };

type DkvRecentRow = {
  id: string;
  transaction_at: string;
  product: string;
  cost_group: string;
  unit: string;
  quantity_milli: number | null;
  total_gross_cents: number;
  license_plate: string;
  card_number: string;
  station: string;
  vehicle_id: string | null;
  fuel_transaction_id: string | null;
};

type DkvResponse = {
  canManage: boolean;
  settings: DkvSettings | null;
  counts: DkvCounts | null;
  recent: DkvRecentRow[];
  syncRunning: boolean;
};

function formatEur(cents: number): string {
  return (cents / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

function formatQty(milli: number | null, unit: string): string {
  if (milli == null) return '';
  const value = (milli / 1000).toLocaleString('de-DE', { maximumFractionDigits: 2 });
  return unit.toUpperCase() === 'L' ? `${value} L` : unit ? `${value} ${unit}` : value;
}

export function DkvSettingsSection() {
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [headerNotice, setHeaderNotice] = React.useState<string | null>(null);
  const [data, setData] = React.useState<DkvResponse | null>(null);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [editSettings, setEditSettings] = React.useState<DkvEditSettings | null>(null);
  const [testing, setTesting] = React.useState(false);
  const [syncing, setSyncing] = React.useState(false);
  const [toggling, setToggling] = React.useState(false);
  const [showRecent, setShowRecent] = React.useState(false);

  const load = React.useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    setLoadError(null);
    try {
      const res = await fetch('/api/settings/dkv', { credentials: 'include' });
      const d = await res.json();
      if (!res.ok) throw new Error(d?.error || 'DKV-Einstellungen konnten nicht geladen werden.');
      setData({
        canManage: Boolean(d.canManage),
        settings: d.settings ?? null,
        counts: d.counts ?? null,
        recent: Array.isArray(d.recent) ? d.recent : [],
        syncRunning: Boolean(d.syncRunning),
      });
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  const settings = data?.settings ?? null;
  const canManage = Boolean(data?.canManage);

  const openConnect = () => {
    setEditSettings(null);
    setModalOpen(true);
  };

  const openEdit = () => {
    if (!settings) return;
    setEditSettings({
      clientId: settings.clientId,
      hasClientSecret: settings.hasClientSecret,
      hasSubscriptionKey: settings.hasSubscriptionKey,
      customerIds: settings.customerIds,
      syncEnabled: settings.syncEnabled,
    });
    setModalOpen(true);
  };

  const testConnection = async () => {
    setTesting(true);
    setActionError(null);
    setHeaderNotice(null);
    try {
      const res = await fetch('/api/settings/dkv/test', { method: 'POST', credentials: 'include' });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d?.error || 'Prüfung fehlgeschlagen.');
      setHeaderNotice('Verbindung geprüft.');
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setTesting(false);
      await load({ silent: true });
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    setActionError(null);
    setHeaderNotice(null);
    try {
      const res = await fetch('/api/settings/dkv/sync', { method: 'POST', credentials: 'include' });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d?.error || 'Abruf fehlgeschlagen.');
      const stats = d?.stats as DkvSyncStats | undefined;
      setHeaderNotice(
        stats
          ? `${stats.fuelImported} Tankung${stats.fuelImported === 1 ? '' : 'en'} übernommen, ${stats.fetched} Transaktion${stats.fetched === 1 ? '' : 'en'} gelesen.`
          : 'Abruf abgeschlossen.',
      );
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setSyncing(false);
      await load({ silent: true });
    }
  };

  const toggleSync = async (enabled: boolean) => {
    setToggling(true);
    setActionError(null);
    try {
      const res = await fetch('/api/settings/dkv', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ syncEnabled: enabled }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d?.error || 'Speichern fehlgeschlagen.');
      await load({ silent: true });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setToggling(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('DKV-Verbindung wirklich entfernen? Bereits importierte Tankungen bleiben im Fuhrpark erhalten.')) return;
    setActionError(null);
    setHeaderNotice(null);
    try {
      const res = await fetch('/api/settings/dkv', { method: 'DELETE', credentials: 'include' });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d?.error || 'Löschen fehlgeschlagen.');
      await load({ silent: true });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    }
  };

  // The row chip plus the line below already show a failed test; no second banner with the same text.
  const bannerError = actionError && actionError !== settings?.lastStatusMessage ? actionError : null;
  const testedOk = settings?.lastStatusCode === 0;
  const testedFail = settings != null && settings.lastStatusCode != null && settings.lastStatusCode !== 0;
  const stats = settings?.lastSyncStats ?? null;
  const unmatchedPlates = stats?.unmatchedPlates ?? [];
  const unmatchedCards = stats?.unmatchedCards ?? [];
  const busy = testing || syncing || Boolean(data?.syncRunning);

  return (
    <>
      <SettingsCard
        title="DKV Mobility"
        description="Tankkarten-Transaktionen automatisch in den Fuhrpark übernehmen. Zuordnung über Kennzeichen und Kartennummer."
        actions={
          <>
            <SettingsCardSavedStatus saved={Boolean(headerNotice)}>{headerNotice}</SettingsCardSavedStatus>
            {canManage && !loading && !settings ? (
              <button type="button" className={SETTINGS_BTN_PRIMARY} onClick={openConnect}>
                <Plus className="w-4 h-4" strokeWidth={1.75} />
                Verbinden
              </button>
            ) : null}
          </>
        }
      >
        <div className="space-y-5">
          {loading ? (
            <InlineLoadingState label="Einstellungen werden geladen …" className="justify-start" />
          ) : (
            <>
              {loadError ? (
                <div className="p-3 rounded-md bg-red-50 text-red-700 text-sm border border-red-200">{loadError}</div>
              ) : null}
              {bannerError ? (
                <div className="p-3 rounded-md bg-red-50 text-red-700 text-sm border border-red-200">{bannerError}</div>
              ) : null}

              {!canManage && !settings ? (
                <p className="text-sm text-app-text-subtle">Nur Inhaber oder Administratoren können DKV verbinden.</p>
              ) : null}

              {!settings && canManage ? (
                <p className="text-sm text-app-text-subtle">
                  Zugang zur DKV Enterprise API gibt es über den DKV-Kundenservice: technischer Benutzer, Client-ID,
                  Client-Secret (per SMS) und Subscription-Key. Danach hier eintragen.
                </p>
              ) : null}

              {settings ? (
                <div className="space-y-4">
                  <div className="rounded-md border border-app-border px-3 py-2">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                      <div className="min-w-0 sm:flex-1">
                        <p className="text-sm font-medium text-app-text leading-5 truncate">
                          Client-ID {settings.clientIdMasked || '—'}
                        </p>
                        <p className="text-xs text-app-text-subtle mt-0.5 truncate">
                          Kundennummer{settings.customerIds.length === 1 ? '' : 'n'}: {settings.customerIds.join(', ') || '—'}
                        </p>
                      </div>
                      <div className="flex items-center justify-between gap-3 sm:justify-end">
                        <div className="flex flex-wrap items-center gap-1.5 min-w-0">
                          {!settings.complete ? <SettingsStatusChip tone="warn">Unvollständig</SettingsStatusChip> : null}
                          {testedOk ? <SettingsStatusChip tone="ok">Geprüft</SettingsStatusChip> : null}
                          {testedFail ? <SettingsStatusChip tone="err">Prüfung fehlgeschlagen</SettingsStatusChip> : null}
                          {settings.lastStatusCode == null ? <SettingsStatusChip tone="muted">Noch nicht geprüft</SettingsStatusChip> : null}
                          {settings.lastSyncStatus === 'ok' && settings.lastSyncAt ? (
                            <SettingsStatusChip tone="ok">
                              Abruf {formatDateTimeDDMMYYYY(settings.lastSyncAt)}
                              {stats ? ` · ${stats.fuelImported} Tankungen` : ''}
                            </SettingsStatusChip>
                          ) : null}
                          {settings.lastSyncStatus === 'error' ? (
                            <SettingsStatusChip tone="err">Abruf fehlgeschlagen</SettingsStatusChip>
                          ) : null}
                          {data?.syncRunning ? <SettingsStatusChip tone="muted">Abruf läuft …</SettingsStatusChip> : null}
                          {!settings.syncEnabled ? <SettingsStatusChip tone="muted">Automatik aus</SettingsStatusChip> : null}
                        </div>
                        {canManage ? (
                          <div className="flex items-center gap-2 shrink-0">
                            <button
                              type="button"
                              className={SETTINGS_BTN_OUTLINE}
                              onClick={() => void testConnection()}
                              disabled={busy}
                            >
                              {testing ? <LoadingSpinner size="sm" /> : null}
                              Prüfen
                            </button>
                            <button
                              type="button"
                              className={SETTINGS_BTN_OUTLINE}
                              onClick={() => void syncNow()}
                              disabled={busy || !settings.complete}
                              title="Transaktionen jetzt von DKV abrufen"
                            >
                              {syncing || data?.syncRunning ? <LoadingSpinner size="sm" /> : <RefreshCw className="w-3.5 h-3.5" />}
                              Jetzt abrufen
                            </button>
                            <button
                              type="button"
                              className={`${SETTINGS_BTN_OUTLINE} !px-2`}
                              onClick={openEdit}
                              title="Zugang bearbeiten"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              className={`${SETTINGS_BTN_DANGER_OUTLINE} !px-2`}
                              onClick={() => void remove()}
                              title="Verbindung entfernen"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  {testedFail && settings.lastStatusMessage ? (
                    <p className="text-xs text-red-700">{settings.lastStatusMessage}</p>
                  ) : null}
                  {settings.lastSyncStatus === 'error' && settings.lastSyncError ? (
                    <p className="text-xs text-red-700">{settings.lastSyncError}</p>
                  ) : null}

                  {canManage ? (
                    <label className="flex items-center gap-2 text-sm text-app-text">
                      <input
                        type="checkbox"
                        checked={settings.syncEnabled}
                        disabled={toggling}
                        onChange={(e) => void toggleSync(e.target.checked)}
                      />
                      Täglich automatisch abrufen (nachts, Europe/Berlin)
                    </label>
                  ) : null}

                  {data?.counts ? (
                    <div className="grid gap-3 sm:grid-cols-3">
                      <div className="rounded-md border border-app-border p-3">
                        <p className="text-xs font-medium text-app-text-subtle">Transaktionen</p>
                        <p className="mt-1 text-lg font-semibold text-app-text">{data.counts.staged}</p>
                        <p className="text-xs text-app-text-subtle">inkl. Maut und Gebühren</p>
                      </div>
                      <div className="rounded-md border border-app-border p-3">
                        <p className="text-xs font-medium text-app-text-subtle">Tankungen im Fuhrpark</p>
                        <p className="mt-1 text-lg font-semibold text-app-text">{data.counts.fuel}</p>
                        <p className="text-xs text-app-text-subtle">einem Fahrzeug zugeordnet</p>
                      </div>
                      <div className="rounded-md border border-app-border p-3">
                        <p className="text-xs font-medium text-app-text-subtle">Ohne Fahrzeug</p>
                        <p className="mt-1 text-lg font-semibold text-app-text">{data.counts.unmatched}</p>
                        <p className="text-xs text-app-text-subtle">Kennzeichen im Fuhrpark prüfen</p>
                      </div>
                    </div>
                  ) : null}

                  {unmatchedPlates.length || unmatchedCards.length ? (
                    <div className="space-y-2">
                      <h3 className={SETTINGS_FIELD_LABEL}>Nicht zugeordnet</h3>
                      <p className="text-xs text-app-text-subtle">
                        Diese Kennzeichen aus DKV passen zu keinem Fahrzeug. Kennzeichen im Fuhrpark genauso eintragen wie bei DKV, dann beim nächsten Abruf übernehmen.
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {unmatchedPlates.map((plate) => (
                          <SettingsStatusChip key={`p-${plate}`} tone="warn">
                            {plate}
                          </SettingsStatusChip>
                        ))}
                        {unmatchedCards.map((card) => (
                          <SettingsStatusChip key={`c-${card}`} tone="muted">
                            Karte {card}
                          </SettingsStatusChip>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {data?.recent.length ? (
                    <div className="space-y-2">
                      <button
                        type="button"
                        className="text-xs font-medium text-pipedrive-blue hover:underline"
                        onClick={() => setShowRecent((v) => !v)}
                      >
                        {showRecent ? 'Letzte Transaktionen ausblenden' : `Letzte ${data.recent.length} Transaktionen anzeigen`}
                      </button>
                      {showRecent ? (
                        <div className="overflow-hidden rounded-md border border-app-border">
                          <table className="w-full text-xs">
                            <thead className="bg-app-surface-muted text-left text-app-text-subtle">
                              <tr>
                                <th className="px-3 py-2 font-medium">Datum</th>
                                <th className="px-3 py-2 font-medium">Kennzeichen</th>
                                <th className="px-3 py-2 font-medium">Produkt</th>
                                <th className="px-3 py-2 font-medium text-right">Menge</th>
                                <th className="px-3 py-2 font-medium text-right">Brutto</th>
                                <th className="px-3 py-2 font-medium">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {data.recent.map((row) => (
                                <tr key={row.id} className="border-t border-app-border">
                                  <td className="px-3 py-2 text-app-text-muted whitespace-nowrap">
                                    {formatDateTimeDDMMYYYY(row.transaction_at)}
                                  </td>
                                  <td className="px-3 py-2 font-mono text-app-text">{row.license_plate || '—'}</td>
                                  <td className="px-3 py-2 text-app-text-muted truncate max-w-[14rem]">
                                    {row.product || row.cost_group || '—'}
                                    {row.station ? <span className="text-app-text-subtle"> · {row.station}</span> : null}
                                  </td>
                                  <td className="px-3 py-2 text-right text-app-text-muted whitespace-nowrap">
                                    {formatQty(row.quantity_milli, row.unit)}
                                  </td>
                                  <td className="px-3 py-2 text-right text-app-text whitespace-nowrap">{formatEur(row.total_gross_cents)}</td>
                                  <td className="px-3 py-2">
                                    {row.fuel_transaction_id ? (
                                      <SettingsStatusChip tone="ok">Im Fuhrpark</SettingsStatusChip>
                                    ) : row.vehicle_id ? (
                                      <SettingsStatusChip tone="muted">Zugeordnet</SettingsStatusChip>
                                    ) : (
                                      <SettingsStatusChip tone="warn">Kein Fahrzeug</SettingsStatusChip>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </>
          )}
        </div>
      </SettingsCard>

      <DkvConnectModal
        open={modalOpen}
        editSettings={editSettings}
        onClose={() => {
          setModalOpen(false);
          setEditSettings(null);
        }}
        onSaved={async (result) => {
          setModalOpen(false);
          setEditSettings(null);
          await load({ silent: true });
          if (result.testedOk) {
            setActionError(null);
            setHeaderNotice('Gespeichert und verbunden.');
          } else {
            setHeaderNotice(null);
            setActionError(result.testError || 'Gespeichert, Prüfung fehlgeschlagen.');
          }
        }}
      />
    </>
  );
}
